import { msg } from "@omegagrid/localize";
import { SerializedValue, TreeAdapter, TreeSource } from "@omegagrid/tree";
import { CellType, ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { DimTreeAdapter } from '../../adapters';
import { StatementRow, StatementDefinition, StatementRowFromRow, DimensionDefinition } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";

class AllRowTreeAdapter extends TreeAdapter {
	public accounting: number;

	constructor(private statementMap: Record<number, string>) { super() }

	private _rows: TreeSource;

	async getTree() {
		if (this._rows) return this._rows;

		const rows = await rp.ms('definition').get<StatementRow[]>('statementRows', {
			fields: ['id_row', 'row', 'id_statement'],
			order: [['id_statement', 'asc'], ['row', 'asc']],
			count: 100000, // enforce all rows
			accounting: this.accounting,
		});

		this._rows = rows.map(row => ({
			k: row.id_row.toString(),
			v: [
				this.statementMap[row.id_statement] ?? `[${msg('deleted')}]`,
				row.id_row.toString(),
				row.row
			].join(' - '),
		}));

		return this._rows;
	}
}


export class FinAggregatedRowsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _statementMap: Record<number, string>;
	private _dimensions: DimensionDefinition[];
	private a: number;
	private editable: boolean = true;

	constructor(
		private statement: StatementDefinition,
	) {
		super();
		this.a = statement.accounting;
		this.editable = this.a === rp.accountingProvider.a;
	}

	private async getStatementMap() {
		if (this._statementMap) return this._statementMap;

		const statements = await rp.ms('definition').get<StatementDefinition[]>(`/statements`, {
			fields: ['id_statement', 'statement']
		});
		this._statementMap = statements.reduce((acc, val, i) => {
			acc[val.id_statement] = val.statement;
			return acc;
		}, {} as Record<number, string>);

		return this._statementMap;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._dimensions = await rp.ms('definition').get<DimensionDefinition[]>(`visibleDimensions/${MODULE_CODES.FINANCE}`, { accounting: this.a });

		this._columns = [{
			data: 'id_row',
			t: 'n',
			z: '0',
			e: false,
			w: 80,
			header: [null, {label: '#'}],
		}, {
			data: 'row',
			t: 't',
			e: false,
			w: 200,
			header: [null, {label: msg('Row')}],
		}, {
			data: 'id_row_from',
			t: 's',
			adapter: Object.assign(new AllRowTreeAdapter(await this.getStatementMap()), { accounting: this.a }),
			w: 300,
			header: [null, {label: msg('Source Row')}],
		}, {
			data: 'side',
			t: 's',
			w: 80,
			header: [null, {label: msg('Side')}],
			content: [
				{ k: 'T', v: msg('Turnover') },
				{ k: 'D', v: msg('Debit') },
				{ k: 'C', v: msg('Credit') }
			]
		}, ...this._dimensions.flatMap(dim => ([{
			data: `level_dim_${dim.dim_num}`,
			t: 's' as CellType,
			w: 75,
			content: [
				{k: 0, v: msg('All')},
				...(new Array(5)).fill(0).map((_, i) => ({k: i + 1, v: `${i + 1}`})),
			],
			header: [{label: dim.description, cs: 3}, {label: msg('Level')}],
		}, {
			data: `filter_dim_${dim.dim_num}`,
			t: 'm' as CellType,
			w: 200,
			adapter: Object.assign(new DimTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num), { accounting: this.a }),
			header: [null, {label: `${msg('Filters')} - ${msg('included')}`}],
		}, {
			data: `not_dim_${dim.dim_num}`,
			t: 'm' as CellType,
			w: 200,
			adapter: Object.assign(new DimTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num), { accounting: this.a }),
			header: [null, {label: `${msg('Filters')} - ${msg('excluded')}`}],
		}]))];

		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const statementMap = await this.getStatementMap();
		const rows = await rp.ms('definition').get<StatementRowFromRow[]>('statementRowAggregates', { id_statement: this.statement.id_statement, accounting: this.a });
		return rows.map(data => ({
			data: {
				...data,
				side: data.side || 'T', // null = turnover, shown as 'T' like the single-row editor does
				id_row_from: {
					v: data.id_row_from,
					dv: [
						statementMap[data.id_statement_from],
						data.id_row_from?.toString(),
						data.row_from
					].filter(x => x).join(' - '),
				},
				...this._dimensions.reduce((acc, dim, i) => {
					acc[`filter_dim_${dim.dim_num}`] = {
						v: data[`filter_dim_${dim.dim_num}` as keyof StatementRowFromRow],
						dv: data[`filter_dim_values_${dim.dim_num}` as keyof StatementRowFromRow] || null,
					};
					acc[`not_dim_${dim.dim_num}`] = {
						v: data[`not_dim_${dim.dim_num}` as keyof StatementRowFromRow],
						dv: data[`not_dim_values_${dim.dim_num}` as keyof StatementRowFromRow] || null,
					};
					return acc;
				}, {} as Record<string, any>)

			}
		}));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: 0,
			freezeLeft: 2,
			refreshAfterSave: true,
			editable: this.editable,
			toolbar: {
				new: { enabled: false },
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
			}
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => {
			const item = Object.assign({_state: row.state, _index: row.index}, row.data);
			if (item.side === 'T') item.side = null; // turnover is stored as null — the calculation reads 'T' as neither side
			Object.keys(row.data).forEach(key => {
				if ((key.startsWith('filter_dim_') || key.startsWith('not_dim_')) && Array.isArray(row.data[key])) {
					item[key] = (row.data[key] as SerializedValue[]).map(x => Array.isArray(x) ? x[0] : x);
				}
			});
			return item;
		});

		await rp.ms('definition').post(`statementRowAggregates`, data);
	}

}
